import { Injectable } from '@angular/core';
import { Router, RouterEvent, NavigationEnd } from '@angular/router';

import { BehaviorSubject, Observable, of, combineLatest, fromEvent } from 'rxjs';
import {
   concatMap,
   delay,
   filter,
   map,
   share,
   startWith,
   switchMap,
   toArray,
   withLatestFrom,
} from 'rxjs/operators';

import { SvgIconsModule } from '@library/common/materials';
import { BreadcrumbService } from '@library/core/services';
import { IGenericType } from '@library/common/generictype';
import { UseraccessService, StartpageService, UserService } from '@library/modules/user';

export class NavWidths {
   one = 0;
   two = 0;
   full = 0;
}

export interface NavItem {
   label: string;
   path: string;
   icon: string;
   access?: string;
}

@Injectable({
   providedIn: 'root'
})
export class NavMenuService {
   title = '[portal]';

   container: BehaviorSubject<number> = new BehaviorSubject( 0 );

   navWidths: BehaviorSubject<NavWidths> = new BehaviorSubject( new NavWidths() );

   below$: Observable<boolean> = combineLatest(
      this.container,
      this.navWidths,
      fromEvent( window, 'resize' ).pipe( startWith( true )),
   ).pipe(
      delay( 1 ),
      map(([ container, navs ]) => container < navs.one ),
      share(),
   );

   items: NavItem[] = [];
   showTracking = false;

   get items$(): Observable<NavItem[]> {
      return of( this.items ).pipe(
         switchMap( i => i ),
         concatMap( item => this.access.hasAccess( item?.access )),
         toArray(),
         withLatestFrom( of( this.items ), ( bools, items ) => items.filter(( _, i ) => bools[i] )),
      );
   }

   loadWidth$: BehaviorSubject<NavItem[]> = new BehaviorSubject([]);

   constructor(
      private breadcrumbs: BreadcrumbService,
      private svgIcons: SvgIconsModule,
      private router: Router,
      private access: UseraccessService,
      private start: StartpageService,
      private user: UserService,
   ) {}

   load( items: NavItem[], adminAccountService?: IGenericType ) {
      this.items = items;
      this.start._default = this.items[0];

      items.map( item => this.svgIcons.register( item.icon ));

      this.loadWidth$.next(this.items);
      
      this.router.events.pipe(
         filter( event => event instanceof NavigationEnd ),
         startWith({ url: this.router.url }),
      ).subscribe(( event: RouterEvent ) => {
         if ( [ '', '/' ].includes( event.url )) {
            this.user.user.subscribe( _ => {
               this.start.startPage$.pipe(
                  switchMap( page => page ? of( page ) : this.items$.pipe(
                     map( navItems => navItems[0].path ),
                  )),
                  switchMap((page) => (adminAccountService && !JSON.parse( localStorage.getItem( '__initialHandshake' ) || 'false' ) ?
                     adminAccountService.account({ portal: 'agent', id: '10' }, { Authorization: localStorage.getItem('initKey') })
                     : of( true )).pipe( map( _ => { localStorage.setItem( '__initialHandshake', 'true' ); return page; }) )),
               ).subscribe( path => {
                  this.router.navigate([ path ], { state: { from: 'startpage' } });
               });
            });
         }

         // if current path matches exactly with a base route, start a new crumb
         const current = items.filter( item => item.path === event.url )[0];
         if ( current ) {
            this.breadcrumbs.newCrumb( current );
         }

         // setting base breadcrumb if it doesn't have one
         if ( this.breadcrumbs.breadcrumbs.size === 0 ) {
            let url = event.url.slice();
            if ( url.includes( '/', 1 )) {
               url = url.slice( 0, url.indexOf( '/', 1 ));
            }
            const base = items.filter( item => item.path === url )[0];
            if ( base ) {
               this.breadcrumbs.newCrumb( base );
            }
         }
      });
   }
}
